/**
 * i18n bootstrap, shared shape with the widget.
 *
 * The important part is `applyDocumentLocale`: switching language must also
 * switch `dir` and the font stack on <html>. Everything downstream - Tailwind's
 * logical utilities, the floor-plan mirroring, the RTL time grid - keys off
 * that one attribute. Get it right once here and no component needs to know
 * which direction it is in.
 */

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { DIRECTION, LOCALES, type Locale } from '@yp/shared';
import en from '@yp/shared/locales/en';
import ar from '@yp/shared/locales/ar';

const STORAGE_KEY = 'yp.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Set `lang` and `dir` on <html>. The Arabic font stack follows from
 * `html[lang="ar"]` in index.css - one attribute drives everything.
 *
 * Called before React mounts and again on every language change. Doing it on
 * <html> rather than a wrapper div means native scrollbars, form controls,
 * date pickers and the browser's own text selection all flip too.
 */
export function applyDocumentLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = DIRECTION[locale];
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private browsing on an iPad at the host stand. Not worth failing over.
  }
}

export async function initI18n(): Promise<typeof i18next> {
  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { translation: en },
        ar: { translation: ar },
      },
      supportedLngs: [...LOCALES],
      fallbackLng: 'en',
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: STORAGE_KEY,
        caches: [],
      },
      interpolation: {
        // React escapes for us; double-escaping mangles Arabic punctuation.
        escapeValue: false,
      },
      returnNull: false,
    });

  const active = isLocale(i18next.resolvedLanguage) ? i18next.resolvedLanguage : 'en';
  applyDocumentLocale(active);

  i18next.on('languageChanged', (lng) => {
    if (isLocale(lng)) applyDocumentLocale(lng);
  });

  return i18next;
}

export { i18next };
